import React, { useState } from 'react';
import { Form } from 'antd';
import { CSVLink } from 'react-csv';
import { ActionResponse } from '../../../models/actionResponse/types';
import { ExternalProduct, Product } from '../../../models/product/types';
import {
    LiveGuideline,
    LiveGuidelineProduct,
} from '../../../redux/actions/liveGuidelineActions/types';
import { getLiveGuidelineProductsAction } from '../../../redux/actions/liveGuidelineActions';
import {
    getCompaniesOfProducts,
    getExternalProductsAction,
} from '../../../redux/actions/transparencyCatalogActions';
import { GetProductsFromTransparencyCatalogAPIResponse } from '../../../redux/actions/transparencyCatalogActions/types';
import { useAppDispatch } from '../../../hooks/redux';
import { AppDispatch } from '../../../redux/store';
import { alertService } from '../../../services';
import {
    collectDisclosuresFromProducts,
    fetchDisclosureImpactAreaMap,
    getProductImpactAreas,
    impactAreasToCSVString,
} from '../../../helpers/cmfImpactAreaMapper';
import { mapLowEmittingData } from '../../../helpers/lowEmittingHelper';
import { CSVHeader } from '../../../components/projects/projectActions/exportProject/ExportProject.mockData';
import ModalTitle from '../../../components/common/modalTitle/ModalTitle';
import ModalFooter from '../../../components/common/modalFooter/ModalFooter';
import { CloseIcon, Text } from '../../../styles/common.styles';
import { blueDark01 } from '../../../styles/colors';
import {
    ExportProjectModal,
    ExportSpin,
} from '../../../components/projects/projectActions/exportProject/ExportProject.styles';
import { Division } from '../../../models/productDivision/types';

interface Props {
    guideline: Pick<LiveGuideline, 'id' | 'name'>;
    isOpen: boolean;
    closeModal: () => void;
    division?: Pick<Division<Product>, 'id' | 'name'>;
}

interface ExportState {
    exportData: Product[];
    fileName: string;
    pending: boolean;
}

const getGuidelineExportProducts = async (
    guideline: Pick<LiveGuideline, 'id' | 'name'>,
    dispatch: AppDispatch,
): Promise<Product[]> => {
    const productsResult = await dispatch(getLiveGuidelineProductsAction(guideline.id));
    const payload = (productsResult as ActionResponse<LiveGuidelineProduct[]>).payload;
    const associations = (payload?.data ?? []).filter(
        (product): product is LiveGuidelineProduct & { productId: number } =>
            !product.isDeleted && typeof product.productId === 'number',
    );
    const productIds = Array.from(
        new Set(associations.map((association) => association.productId)),
    );

    if (!productIds.length) {
        throw new Error('There are no products to export');
    }

    const { payload: externalPayload } = (await dispatch(
        getExternalProductsAction({ productIds }),
    )) as ActionResponse<GetProductsFromTransparencyCatalogAPIResponse>;
    if (!externalPayload?.data) {
        throw new Error('Could not fetch product details');
    }

    const availableProducts = Object.values(externalPayload.data.mfSection).reduce(
        (allProducts, section: any) => allProducts.concat(section),
        [] as ExternalProduct[],
    );
    const productsById = new Map(availableProducts.map((product) => [product.id, product]));
    const products = associations
        .map((association) => {
            const externalProduct = productsById.get(association.productId);
            if (!externalProduct || !association) {
                return undefined;
            }
            return {
                ...externalProduct,
                projectProductId: externalProduct.id,
            } as unknown as Product;
        })
        .filter((product): product is Product => product !== undefined);

    if (!products.length) {
        throw new Error('Could not fetch product details');
    }
    return products;
};

const formatGuidelineExportProducts = async (
    products: Product[],
    dispatch: AppDispatch,
): Promise<Product[]> => {
    const companies = await getCompaniesOfProducts(products as ExternalProduct[], dispatch);
    let enrichedProducts: Product[] = products.map((product) => {
        const company = companies.find((item) => item.id === product.companyID);
        return {
            ...product,
            companyLevel: company?.level,
            listingUrl: company?.listingUrl,
            websiteUrl: company?.websiteUrl,
        };
    });

    await mapLowEmittingData(enrichedProducts, dispatch);
    const impactAreaMap = await fetchDisclosureImpactAreaMap(
        dispatch,
        collectDisclosuresFromProducts(enrichedProducts),
    );

    enrichedProducts = enrichedProducts.map((product) => {
        const impactAreas = getProductImpactAreas(product, impactAreaMap);
        return {
            ...product,
            productNameCorrected: product.productName.replaceAll('"', '""'),
            descriptionCorrected: product.description?.replaceAll('"', '""'),
            hpdScopeResultsCorrected: product.regulations?.includes(
                'Perkins&Will Precautionary List Free' as any,
            )
                ? 'Perkins&Will PL Free'
                : product.hpdScopeResults,
            masterFormat: `${product.mfSection.number} ${product.mfSection.label}`,
            tagsCSV: (product.tags ?? []).join(', '),
            epdCmfImpactAreas: impactAreasToCSVString(impactAreas.epd),
            hpdCmfImpactAreas: impactAreasToCSVString(impactAreas.hpd),
            declareCmfImpactAreas: impactAreasToCSVString(impactAreas.declare),
            c2cCmfImpactAreas: impactAreasToCSVString(impactAreas.c2c),
            c2cMHCCmfImpactAreas: impactAreasToCSVString(impactAreas.c2cMHC),
            lpcCmfImpactAreas: impactAreasToCSVString(impactAreas.lpc),
            cleanAirSilverCmfImpactAreas: impactAreasToCSVString(impactAreas.cleanAirSilver),
            cleanAirGoldCmfImpactAreas: impactAreasToCSVString(impactAreas.cleanAirGold),
            clearchemCmfImpactAreas: impactAreasToCSVString(impactAreas.clearChem),
            floorscoreCmfImpactAreas: impactAreasToCSVString(impactAreas.floorscore),
            greenCertifiedCmfImpactAreas: impactAreasToCSVString(impactAreas.greenCertified),
            greenguardCmfImpactAreas: impactAreasToCSVString(impactAreas.greenguard),
            greenguardGoldCmfImpactAreas: impactAreasToCSVString(impactAreas.greenguardGold),
            indoorAdvantageCmfImpactAreas: impactAreasToCSVString(impactAreas.indoorAdvantage),
            indoorAdvantageGoldCmfImpactAreas: impactAreasToCSVString(
                impactAreas.indoorAdvantageGold,
            ),
        };
    });

    return enrichedProducts.sort((first, second) => {
        if (first.masterFormat === second.masterFormat) {
            return first.company.localeCompare(second.company);
        }
        return first.masterFormat.localeCompare(second.masterFormat);
    });
};

const LiveGuidelineExportModal: React.FC<Props> = ({
    guideline,
    isOpen,
    closeModal,
    division,
}) => {
    const dispatch = useAppDispatch();
    const [state, setState] = useState<ExportState>({
        exportData: [],
        fileName: '',
        pending: false,
    });

    const handleExport = async () => {
        setState((previous) => ({ ...previous, pending: true }));

        try {
            const guidelineProducts = await getGuidelineExportProducts(guideline, dispatch);
            const formattedProducts = await formatGuidelineExportProducts(
                guidelineProducts,
                dispatch,
            );
            const exportProducts = division
                ? formattedProducts.filter(
                      (product) =>
                          product.mfDivision.number?.toString() === division.id.toString(),
                  )
                : formattedProducts;
            if (!exportProducts.length) {
                throw new Error('There are no products to export');
            }
            const divisionNumber = division?.id.substring(0, 2).replace(/^0/, '');
            const fileNameBase = division
                ? `${guideline.name}-Division-${divisionNumber}`
                : guideline.name;
            const fileName = `${fileNameBase.replace(/[<>:"/\\|?*\x00-\x1F]/g, '-')}-Products.csv`;
            setState({ exportData: exportProducts, fileName, pending: false });

            setTimeout(() => {
                document.getElementById('csv-link')?.click();
                closeModal();
            }, 500);
        } catch (error) {
            alertService.warn(error instanceof Error ? error.message : 'Failed to export products');
            setState((previous) => ({ ...previous, pending: false }));
        }
    };

    return (
        <ExportProjectModal
            open={isOpen}
            onCancel={closeModal}
            closeIcon={<CloseIcon />}
            centered
            footer={null}
        >
            <ExportSpin spinning={state.pending}>
                <ModalTitle title="Export to CSV" icon="ic-modal-title-export.svg" />
                <Text weight={400} size={15} height={20} color={blueDark01}>
                    Export to CSV includes a spreadsheet of product name, manufacturer name,
                    MasterFormat number, URL to their Transparency Catalog listing page, optionally
                    added attributes (pricing, BOD, substitutions), and the types of transparency
                    documentation for that product.
                    <br />
                    Hidden products are excluded from the export.
                </Text>
                <CSVLink
                    id="csv-link"
                    filename={state.fileName}
                    data={state.exportData}
                    headers={CSVHeader}
                >
                    Download
                </CSVLink>
                <Form onFinish={handleExport}>
                    <ModalFooter
                        action="Export"
                        cancel={closeModal}
                        cancelText="Cancel"
                        submit={() => {}}
                    />
                </Form>
            </ExportSpin>
        </ExportProjectModal>
    );
};

export default LiveGuidelineExportModal;
